import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../dashboard/components/AdminLayout';
import { useAdminDocuments } from '../hooks/useAdminDocuments';
import { api } from '../../../../core/utils/apiClient';
import { DocumentViewerModal } from '../components/DocumentViewerModal';
import { UploadDocumentDrawer } from '../components/UploadDocumentDrawer';
import { DeleteDocumentConfirmationDialog } from '../components/DeleteDocumentConfirmationDialog';
import { AdminDocument } from '../types';
import { COLORS } from '../../../../core/constants/colors';

export const AdminDocumentsPage: React.FC = () => {
  const { documents, isLoading, loadDocuments, deleteDocument } = useAdminDocuments();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [companies, setCompanies] = useState<any[]>([]);
  const [showUploadDrawer, setShowUploadDrawer] = useState(false);
  const [showViewer, setShowViewer] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<AdminDocument | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean; document: AdminDocument | null }>({
    isOpen: false,
    document: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await api.get('/api/v1/companies/');
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch = doc.original_file_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.company_name || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || doc.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const paginatedDocuments = filteredDocuments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredDocuments.length / itemsPerPage);

  const handleViewDocument = (doc: AdminDocument) => {
    setSelectedDocument(doc);
    setShowViewer(true);
  };

  const handleDeleteClick = (doc: AdminDocument, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteDialog({ isOpen: true, document: doc });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.document) return;

    setIsDeleting(true);
    try {
      await deleteDocument(deleteDialog.document.id);
      setDeleteDialog({ isOpen: false, document: null });
    } catch (error) {
      console.error('Error deleting document:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ isOpen: false, document: null });
  };

  const handleDownload = async (doc: AdminDocument, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const token = sessionStorage.getItem('idToken');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/v1/documents/documents/${doc.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.original_file_name;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };


  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, { bg: string; text: string }> = {
      completed: { bg: '#D1FAE5', text: '#059669' },
      processed: { bg: '#D1FAE5', text: '#059669' },
      processing: { bg: '#FEF3C7', text: '#D97706' },
      failed: { bg: '#FEE2E2', text: '#DC2626' },
      uploaded: { bg: '#DBEAFE', text: '#1E40AF' },
    };
    return statusColors[status] || { bg: '#F3F4F6', text: '#6B7280' };
  };

  if (isLoading) {
    return (
      <AdminLayout pageTitle="Documents" pageDescription="Manage and view all portfolio documents">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: '#6B7280' }}>Loading documents...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Documents" pageDescription="Manage and view all portfolio documents">
      <div style={{ backgroundColor: '#F5F5F5' }}>
        <div className="w-full px-8 py-8 overflow-x-hidden">
          {/* Action Bar */}
          <div className="flex items-center justify-between gap-4 mb-6">
            {/* Search - Left */}
            <div className="relative flex-shrink-0" style={{ width: '300px' }}>
              <svg className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB', color: '#1F2937' }}
              />
            </div>

            {/* Filter & Upload - Right */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB', color: '#1F2937' }}
              >
                <option value="all">All Status</option>
                <option value="uploaded">Uploaded</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>

              <button
                onClick={() => setShowUploadDrawer(true)}
                className="px-4 py-2 rounded-lg font-medium text-white transition-all hover:shadow-md whitespace-nowrap"
                style={{ backgroundColor: COLORS.admin.primary }}
              >
                <svg className="w-4 h-4 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Upload Document
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="w-full">
              <table className="w-full">
                <thead style={{ backgroundColor: '#F9FAFB' }}>
                  <tr>
                    <th className="px-3 py-4 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                      Document
                    </th>
                    <th className="px-3 py-4 text-left text-sm font-semibold hidden md:table-cell" style={{ color: '#6B7280' }}>
                      Company
                    </th>
                    <th className="px-3 py-4 text-left text-sm font-semibold hidden lg:table-cell w-20" style={{ color: '#6B7280' }}>
                      Size
                    </th>
                    <th className="px-3 py-4 text-left text-sm font-semibold w-24" style={{ color: '#6B7280' }}>
                      Status
                    </th>
                    <th className="px-3 py-4 text-left text-sm font-semibold hidden lg:table-cell w-28" style={{ color: '#6B7280' }}>
                      Created
                    </th>
                    <th className="px-3 py-4 text-right text-sm font-semibold w-28" style={{ color: '#6B7280' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedDocuments.length > 0 ? (
                    paginatedDocuments.map((doc, index) => {
                      const statusBadge = getStatusBadge(doc.status);
                      return (
                        <tr
                          key={doc.id}
                          className="border-b hover:bg-gray-50 transition-colors cursor-pointer"
                          style={{ borderColor: '#E5E7EB' }}
                          onClick={() => handleViewDocument(doc)}
                        >
                          <td className="px-3 py-4">
                            <div className="flex items-center gap-3 min-w-0 max-w-xs">
                              <div
                                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: '#FFF3E0' }}
                              >
                                <svg className="w-5 h-5" style={{ color: '#FF5E2E' }} fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-sm truncate" style={{ color: '#1F2937' }}>
                                  {doc.original_file_name}
                                </p>
                                {doc.document_type && (
                                  <p className="text-xs truncate" style={{ color: '#6B7280' }}>
                                    {doc.document_type}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-4">
                            <span className="text-sm truncate block hidden md:table-cell max-w-xs" style={{ color: '#1F2937' }}>
                              {doc.company_name || `Company #${doc.company_id}`}
                            </span>
                          </td>
                          <td className="px-3 py-4">
                            <span className="text-sm whitespace-nowrap hidden lg:table-cell" style={{ color: '#6B7280' }}>
                              {formatFileSize(doc.file_size)}
                            </span>
                          </td>
                          <td className="px-3 py-4">
                            <span
                              className="px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap"
                              style={{
                                backgroundColor: statusBadge.bg,
                                color: statusBadge.text,
                              }}
                            >
                              {doc.status}
                            </span>
                          </td>
                          <td className="px-3 py-4">
                            <span className="text-sm whitespace-nowrap hidden lg:table-cell" style={{ color: '#6B7280' }}>
                              {new Date(doc.created_at).toLocaleDateString()}
                            </span>
                          </td>
                          <td className="px-3 py-4" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleViewDocument(doc)}
                                className="p-2 rounded-lg hover:bg-gray-100 transition-all"
                                title="View"
                              >
                                <svg className="w-5 h-5" style={{ color: '#6B7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                              <button
                                onClick={(e) => handleDownload(doc, e)}
                                className="p-2 rounded-lg hover:bg-gray-100 transition-all"
                                title="Download"
                              >
                                <svg className="w-5 h-5" style={{ color: '#6B7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                              </button>
                              <button
                                onClick={(e) => handleDeleteClick(doc, e)}
                                className="p-2 rounded-lg hover:bg-gray-100 transition-all"
                                title="Delete"
                              >
                                <svg className="w-5 h-5" style={{ color: '#EF4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-3 py-12 text-center">
                        <svg className="w-12 h-12 mx-auto mb-3" style={{ color: '#D1D5DB' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-sm" style={{ color: '#9CA3AF' }}>
                          No documents found
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-3 py-4 border-t flex flex-wrap items-center justify-between gap-4" style={{ borderColor: '#E5E7EB' }}>
                <div className="text-sm" style={{ color: '#6B7280' }}>
                  Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredDocuments.length)} of {filteredDocuments.length} documents
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1.5 text-sm font-medium" style={{ color: '#6B7280' }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    style={{ border: '1px solid #E5E7EB', color: '#1F2937' }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Drawer */}
      <UploadDocumentDrawer
        isOpen={showUploadDrawer}
        onClose={() => setShowUploadDrawer(false)}
        companies={companies}
        onUploadComplete={() => {
          loadDocuments();
        }}
      />

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        document={selectedDocument}
        isOpen={showViewer}
        onClose={() => {
          setShowViewer(false);
          setSelectedDocument(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteDocumentConfirmationDialog
        isOpen={deleteDialog.isOpen}
        documentName={deleteDialog.document?.original_file_name || ''}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isDeleting={isDeleting}
      />
    </AdminLayout>
  );
};